import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FixedSizeList as List } from 'react-window'

import {
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    Theme,
    useTheme,
} from '@material-ui/core'
import {
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    TextField,
    Tooltip,
    Divider,
    Paper,
    Slide,
    InputAdornment,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Search } from '@material-ui/icons'
import InfoIcon from '@material-ui/icons/Info'

import { RootState, setWorkSpaceUser } from '../../../store'
import { Container } from '../../../components'
import { NewInvItemForm } from './NewInvItemForm'
import { useAppDispatch, useUser } from '../../../hooks'
import { axiosInstance, isAccountManager } from '../../../helpers'
import { PREVIEW_INSPECTION_TYPE_STEP } from './InspectionConfigurationPage'
import {
    AreaConfig,
    AreaOrder,
    Company,
    IdBoolMap,
    InspectionType,
    InspectionTypeCategory,
    InventoryConfig,
} from '../../../models'
import SelectedItems from './SelectedItems'

export const INSPECTION_TYPE_FORM = 0
export const ADD_NEW_ITEM_FORM = 1
export const ADD_NEW_AREA_FORM = 2
export const ADD_NEW_STATUS_GROUP_FORM = 3

interface Props {
    inspectionTypeName: string
    invItemsMap: IdBoolMap
    SubHeaderStyle: React.CSSProperties
    areaConfigList: AreaConfig[]
    inspectionType?: InspectionType
    inspectionTypeCategory: InspectionTypeCategory
    setInspectionTypeCategory: (
        inspectionTypeCategory: InspectionTypeCategory,
    ) => void
    areaOrder: AreaOrder
    setAreaOrder: (areaOrder: AreaOrder) => void
    setInspectionTypeName: (inspectionTypeName: string) => void
    setInvItemsMap: (map: IdBoolMap) => void
    handleClose: () => void
    setTab: (tab: number) => void
    setMoveOutInspection?: (val: number) => void
}

export const ConfigureInspectionType = (props: Props) => {
    const {
        inspectionTypeName,
        invItemsMap,
        SubHeaderStyle,
        inspectionType,

        areaConfigList,
        inspectionTypeCategory,
        setInspectionTypeCategory,
        areaOrder,
        setAreaOrder,
        setMoveOutInspection,
        setInvItemsMap,
        setTab,
        setInspectionTypeName,
        handleClose,
    } = props

    const { workspaceUser } = useUser()
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const inventoryConfigList = useSelector(
        (state: RootState) => state.aptConfig.inventoryConfigList,
    )

    const [formPage, setFormPage] = useState(INSPECTION_TYPE_FORM)
    const [doValidate, setDoValidate] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [selectedAreaConfig, setSelectedAreaConfig] = useState<number | null>(
        null,
    )

    const invAreaMap = useMemo(() => {
        return areaConfigList.reduce(
            (acc: Record<number, AreaConfig[]>, areaConfig) => {
                areaConfig.inventory_area_configs?.forEach(
                    (iac: { inventory_config: { id: number } }) => {
                        const invConfigId = iac.inventory_config.id
                        if (!acc[invConfigId]) {
                            acc[invConfigId] = []
                        }
                        acc[invConfigId].push(areaConfig)
                    },
                )
                return acc
            },
            {},
        )
    }, [areaConfigList])

    const filteredInvConfigList = useMemo(
        () =>
            inventoryConfigList?.filter((invConfig) => {
                const matchesSearch = invConfig.name
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase())

                if (!selectedAreaConfig) return matchesSearch

                const areaConfigs = invAreaMap[invConfig.id] ?? []
                return (
                    matchesSearch &&
                    areaConfigs.some((ac) => ac.id === selectedAreaConfig)
                )
            }) ?? [],
        [inventoryConfigList, searchText, selectedAreaConfig, invAreaMap],
    )

    const handleCheckboxClick = useCallback(
        (id: number) => {
            const tempMap = { ...invItemsMap }
            tempMap[id] = !tempMap[id]
            setInvItemsMap(tempMap)
        },
        [setInvItemsMap, invItemsMap],
    )

    const handleSelectAllClick = useCallback(() => {
        const newMap: IdBoolMap = {}
        const atLeastOneChecked = Object.values(invItemsMap).some(Boolean)
        Object.keys(invItemsMap).forEach((keyStr) => {
            const key = Number(keyStr)
            newMap[key] = !atLeastOneChecked
        })
        setInvItemsMap(newMap)
    }, [invItemsMap, setInvItemsMap])

    const handleInspectionTypeNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setInspectionTypeName(e.target.value)
        },
        [setInspectionTypeName],
    )

    const handleSetMoveInInspection = useCallback(() => {
        const req = {
            body: {
                move_out_inspection_config: inspectionType?.id,
            },
        }
        const url = `company/apartment/${workspaceUser?.active_workspace.id}/`
        axiosInstance.patch(url, req.body).then((res) => {
            if (res.data.move_out_inspection_config) {
                if (setMoveOutInspection) {
                    setMoveOutInspection(res.data.move_out_inspection_config)
                }
                if (workspaceUser?.active_workspace) {
                    const updatedWorkspace: Company = {
                        ...workspaceUser.active_workspace,
                        move_out_inspection_config:
                            res.data.move_out_inspection_config,
                    }
                    dispatch(
                        setWorkSpaceUser({
                            ...workspaceUser,
                            active_workspace: updatedWorkspace,
                        }),
                    )
                }
                toast.success('Move in inspection type changed')
            } else {
                toast.error('Can not find inspection type')
            }
        })
    }, [
        inspectionType?.id,
        setMoveOutInspection,
        workspaceUser?.active_workspace.id,
    ])

    const renderInventoryItem = useCallback(
        ({ index, style }: { index: number; style: React.CSSProperties }) => {
            const invC = filteredInvConfigList[index]
            return (
                <div style={style}>
                    <InventoryConfigCheckbox
                        key={invC.id}
                        invC={invC}
                        invItemsMap={invItemsMap}
                        handleCheckboxClick={handleCheckboxClick}
                        theme={theme}
                        invAreaMap={invAreaMap}
                    />
                </div>
            )
        },
        [
            filteredInvConfigList,
            invItemsMap,
            handleCheckboxClick,
            theme,
            invAreaMap,
        ],
    )

    useEffect(() => {
        if (inspectionType) {
            setInspectionTypeName(inspectionType.name)
        }
    }, [inspectionType])

    return (
        <Container flex={1} direction="column">
            <Container alignItems="center">
                <Container
                    style={{ fontWeight: 600, fontSize: '20px', flex: 1 }}
                >
                    Step 1: {inspectionType ? 'Update' : 'Configure'} Inspection
                    Type
                </Container>
                <Tooltip title="Exit">
                    <IconButton onClick={handleClose}>
                        <CloseIcon
                            fontSize="inherit"
                            style={{ color: theme.palette.darkGreen.main }}
                        />
                    </IconButton>
                </Tooltip>
            </Container>

            <Container style={{ marginBottom: theme.spacing(0.5) }}>
                {formPage !== INSPECTION_TYPE_FORM && (
                    <Container>
                        <Container style={{ marginRight: theme.spacing(1) }}>
                            Inspection Name & Choosing Inventory
                        </Container>
                        /
                        <Container
                            style={{
                                margin: theme.spacing(0, 1, 0, 1),
                                color:
                                    formPage === ADD_NEW_ITEM_FORM
                                        ? theme.palette.primary.dark
                                        : '',
                            }}
                        >
                            {` `}Adding An Item
                        </Container>
                    </Container>
                )}
                {formPage === ADD_NEW_AREA_FORM && (
                    <Container>
                        <Container>/</Container>
                        <Container
                            style={{
                                marginLeft: theme.spacing(1),
                                color: theme.palette.primary.dark,
                            }}
                        >
                            Create New Area
                        </Container>
                    </Container>
                )}
                {formPage === ADD_NEW_STATUS_GROUP_FORM && (
                    <Container>
                        <Container>/</Container>
                        <Container
                            style={{
                                marginLeft: theme.spacing(1),
                                color: theme.palette.primary.dark,
                            }}
                        >
                            Create New Status Group
                        </Container>
                    </Container>
                )}
            </Container>

            <Divider />

            {formPage === INSPECTION_TYPE_FORM ? (
                <Container direction="column" style={{ flex: 1 }}>
                    <Container
                        direction="column"
                        style={{
                            maxHeight: 'calc(100vh - 250px)',
                            overflowY: 'auto',
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        <Container
                            style={{ ...SubHeaderStyle, fontSize: '20px' }}
                        >
                            Inspection Name & Choosing Inventory
                        </Container>
                        <Container
                            style={{
                                fontSize: '13px',
                                color: theme.palette.darkGreen.main,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            You have started to configure your inspection type.
                            In order to do so, please name your inspection type
                            and choose your inventory.
                        </Container>
                        <Container style={{ ...SubHeaderStyle }}>
                            Name Your Inspection Type:
                        </Container>
                        <Container style={{ marginBottom: theme.spacing(2) }}>
                            <TextField
                                error={doValidate && inspectionTypeName === ''}
                                helperText={
                                    doValidate && inspectionTypeName === ''
                                        ? 'Required'
                                        : ''
                                }
                                variant="outlined"
                                value={inspectionTypeName}
                                onChange={handleInspectionTypeNameChange}
                                style={{ width: '100%' }}
                                size="small"
                                label="Inspection Type Name"
                            />
                        </Container>
                        <Container style={{ ...SubHeaderStyle }}>
                            Select a category for your inspection type:
                        </Container>
                        <FormControl variant="outlined">
                            <InputLabel>Inspection Category</InputLabel>
                            <Select
                                value={inspectionTypeCategory}
                                onChange={(e) =>
                                    setInspectionTypeCategory(
                                        e.target
                                            .value as InspectionTypeCategory,
                                    )
                                }
                                label="Inspection Category"
                            >
                                <MenuItem value="quarterly">Quarterly</MenuItem>
                                <MenuItem value="move_out">Move Out</MenuItem>
                                <MenuItem value="facilities">
                                    Facilities
                                </MenuItem>
                                <MenuItem value="move_in">Move In</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        <Container
                            style={{
                                ...SubHeaderStyle,
                                marginTop: theme.spacing(2),
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                            }}
                        >
                            <span>
                                Select area order for your inspection type:
                            </span>
                            <Tooltip
                                title={
                                    <div
                                        style={{
                                            fontSize: '0.875rem',
                                            lineHeight: 1.6,
                                            padding: theme.spacing(0.5, 0),
                                            maxWidth: '300px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                                paddingBottom: theme.spacing(1),
                                                borderBottom: `1px solid rgba(255, 255, 255, 0.2)`,
                                            }}
                                        >
                                            Area ordering determines the order
                                            in which areas will appear when
                                            completing your inspection.
                                        </div>
                                        <div
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: theme.spacing(
                                                        0.25,
                                                    ),
                                                }}
                                            >
                                                By Creation:
                                            </div>
                                            <div
                                                style={{
                                                    paddingLeft: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Areas will appear in the order
                                                they were created.
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: theme.spacing(
                                                        0.25,
                                                    ),
                                                }}
                                            >
                                                Alphabetical:
                                            </div>
                                            <div
                                                style={{
                                                    paddingLeft: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Areas will appear in
                                                alphabetical order.
                                            </div>
                                        </div>
                                        <div>
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: theme.spacing(
                                                        0.25,
                                                    ),
                                                }}
                                            >
                                                Common Areas then Bed Bath
                                                Combos:
                                            </div>
                                            <div
                                                style={{
                                                    paddingLeft: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Common areas will appear first,
                                                then each bed and bath
                                                combination.
                                            </div>
                                        </div>
                                    </div>
                                }
                                arrow
                                placement="right"
                            >
                                <InfoIcon
                                    fontSize="small"
                                    color="action"
                                    style={{ cursor: 'help' }}
                                />
                            </Tooltip>
                        </Container>
                        <FormControl
                            variant="outlined"
                            style={{ marginBottom: theme.spacing(2) }}
                        >
                            <InputLabel>Area Order</InputLabel>
                            <Select
                                value={areaOrder}
                                onChange={(e) =>
                                    setAreaOrder(e.target.value as AreaOrder)
                                }
                                label="Area Order"
                            >
                                <MenuItem value={AreaOrder.BY_CREATION}>
                                    By Creation
                                </MenuItem>
                                <MenuItem value={AreaOrder.ALPHABETICAL}>
                                    Alphabetical
                                </MenuItem>
                                <MenuItem value={AreaOrder.COM_BED_BATH_COMBO}>
                                    Common Areas then Bed Bath Combos
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <Container style={{ ...SubHeaderStyle }}>
                            Items You Selected:
                        </Container>
                        <SelectedItems invItemsMap={invItemsMap} />
                        <Container
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(2),
                                padding: theme.spacing(0, 1),
                            }}
                        >
                            <TextField
                                variant="standard"
                                placeholder="Search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                                style={{ flex: 1 }}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                style={{
                                    backgroundColor: theme.palette.primary.dark,
                                    color: 'white',
                                    textTransform: 'none',
                                }}
                                onClick={() => setFormPage(ADD_NEW_ITEM_FORM)}
                            >
                                Add New Item
                            </Button>
                            <FormControl
                                variant="standard"
                                size="small"
                                style={{
                                    minWidth: 200,
                                    paddingBottom: theme.spacing(2),
                                }}
                            >
                                <InputLabel>Filter by Area</InputLabel>
                                <Select
                                    value={selectedAreaConfig || ''}
                                    onChange={(e) =>
                                        setSelectedAreaConfig(
                                            e.target.value
                                                ? Number(e.target.value)
                                                : null,
                                        )
                                    }
                                    label="Filter by Area"
                                >
                                    <MenuItem value="">
                                        <em>All Areas</em>
                                    </MenuItem>
                                    {areaConfigList.map((area) => (
                                        <MenuItem key={area.id} value={area.id}>
                                            {area.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Container>

                        <Container
                            direction="column"
                            style={{
                                maxHeight: 'calc(100vh - 400px)',
                                minHeight: '500px',
                                overflowY: 'auto',
                            }}
                        >
                            <Container
                                style={{
                                    marginLeft: theme.spacing(0.5),
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Checkbox
                                    checked={Object.values(invItemsMap).every(
                                        Boolean,
                                    )}
                                    style={{
                                        color: theme.palette.primary.dark,
                                    }}
                                    indeterminate={
                                        Object.values(invItemsMap).some(
                                            Boolean,
                                        ) &&
                                        !Object.values(invItemsMap).every(
                                            Boolean,
                                        )
                                    }
                                    onChange={handleSelectAllClick}
                                />
                                <Container>
                                    {Object.values(invItemsMap).some(Boolean)
                                        ? 'Remove All'
                                        : 'Select All'}
                                </Container>
                            </Container>

                            <List
                                height={Math.min(
                                    700,
                                    filteredInvConfigList.length * 48,
                                )}
                                itemCount={filteredInvConfigList.length}
                                itemSize={48}
                                width="100%"
                            >
                                {renderInventoryItem}
                            </List>
                        </Container>
                    </Container>
                    <Container flex={1} />
                    <Container>
                        {inspectionType && isAccountManager(workspaceUser) && (
                            <Container style={{ marginTop: theme.spacing(2) }}>
                                <Button
                                    variant="contained"
                                    style={{
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                        color: 'white',
                                        textTransform: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleSetMoveInInspection}
                                >
                                    Set Move In Inspection
                                </Button>
                            </Container>
                        )}
                        <Container style={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            style={{
                                textTransform: 'none',
                                margin: theme.spacing(1, 0, 1, 2),
                            }}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            style={{
                                margin: theme.spacing(1, 0, 1, 2),
                                backgroundColor: '#008C85',
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                if (inspectionTypeName === '') {
                                    setDoValidate(true)
                                } else {
                                    setDoValidate(false)
                                    setTab(PREVIEW_INSPECTION_TYPE_STEP)
                                }
                            }}
                        >
                            Preview
                        </Button>
                    </Container>
                </Container>
            ) : (
                <Slide direction="left" in={formPage !== INSPECTION_TYPE_FORM}>
                    <Paper elevation={0} style={{ flex: 1 }}>
                        <NewInvItemForm
                            subHeaderStyle={SubHeaderStyle}
                            setFormPage={setFormPage}
                            invItemsMap={invItemsMap}
                            setInvItemsMap={setInvItemsMap}
                            formPage={formPage}
                            invItemList={inventoryConfigList ?? []}
                        />
                    </Paper>
                </Slide>
            )}
        </Container>
    )
}

const InventoryConfigCheckbox = React.memo(
    ({
        invC,
        invItemsMap,
        handleCheckboxClick,
        theme,
        invAreaMap,
    }: {
        invC: InventoryConfig
        invItemsMap: IdBoolMap
        handleCheckboxClick: (id: number) => void
        theme: Theme
        invAreaMap: Record<number, AreaConfig[]>
    }) => {
        const areaConfigs = invAreaMap[invC.id] ?? []

        return (
            <FormControlLabel
                key={`INV_CONF_${invC.id}_CHECK_INS_NEW_INSPECTION_TYPE`}
                control={
                    <Checkbox
                        checked={invItemsMap[invC.id] ?? false}
                        style={{ color: theme.palette.primary.dark }}
                        onClick={() => handleCheckboxClick(invC.id)}
                    />
                }
                label={
                    <Container style={{ alignItems: 'center' }}>
                        <Container
                            style={{ color: theme.palette.text.primary }}
                        >
                            {invC.name}
                        </Container>
                        {areaConfigs.length > 0 && (
                            <Container
                                style={{
                                    marginLeft: theme.spacing(1),
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.9em',
                                }}
                            >
                                ({areaConfigs.map((ac) => ac.name).join(', ')})
                            </Container>
                        )}
                    </Container>
                }
                labelPlacement="end"
                style={{ fontWeight: 'bold', marginLeft: theme.spacing(0.5) }}
            />
        )
    },
)
